// app-pages/TermsAndConditions.tsx
"use client";
import { Box, Heading, Text, VStack, Link, List, ListItem, Flex } from "@chakra-ui/react";
import { Navbar } from "components";
import { Metadata } from 'next';

export const metadata: Metadata = {
  title: {
  default: "",
  template: ""
  },
  description: "Read DeepMode's Terms and Conditions for our AI art generator. Understand your rights and responsibilities when using our advanced AI character creation and image generation platform. Stay informed about our policies.",
  };

export const TermsAndConditions = () => {
  return (
    <Flex direction="column">
      <Navbar />
      <Box mt="50px" mb="100px" px={["25px", "35px", "40px", "60px", "60px", "15%"]}>
        <Heading as="h1" size="xl" mb="4">
          Terms and Conditions
        </Heading>
        <Text mb="4">
          <strong>Last Updated: September 06, 2024</strong>
        </Text>
        <Text mb="4">
          Welcome to Deepmode.com. These Terms and Conditions (&quot;Terms&quot;) govern your use of the
          Deepmode.com website and services (collectively, the &quot;Services&quot;), and any images,
          information, text, graphics, or other materials uploaded, downloaded, or appearing on the
          Services. By accessing or using the Services, you agree to be bound by these Terms.
        </Text>

        <Heading as="h2" size="lg" mt="6" mb="2">
          AI Image Generation Rules
        </Heading>
        <Text mb="2">
          WARNING: Failure to adhere to these rules will result in a temporary or permanent ban from
          your account.
        </Text>
        <VStack align="start" mb="4">
          <Text>
            <strong>Prohibited Content</strong>
          </Text>
          <Text>
            NOTE: All user prompts will be analyzed by an AI to ensure compliance with the rules
            listed below. Non-compliance will trigger immediate action.
          </Text>
          <List spacing={2}>
            <ListItem>
              <strong>1. Images or descriptions of minors:</strong> Any content referring to individuals under the age of 18 or appearing to be under the age of 18.
            </ListItem>
            <ListItem>
              <strong>2. Non-consensual Acts:</strong> Any content indicating a lack of consent, such as rape or coercion.
            </ListItem>
            <ListItem>
              <strong>3. Violence and Mutilation:</strong> Content where a person is injured, mutilated, or subjected to extreme violence.
            </ListItem>
            <ListItem>
              <strong>4. Substances and Illegal Activities:</strong> No references to drugs or any other illegal activities are allowed.
            </ListItem>
            <ListItem>
              <strong>5. Graphically Disgusting Content:</strong> Generating images related to feces or vomit is prohibited.
            </ListItem>
          </List>
        </VStack>
        <Text mb="4">
          User Obligations: If a generated image contains any of the elements mentioned above, you
          must immediately report the image.
        </Text>

        {/* Basic Terms */}
        <Heading as="h2" size="lg" mt="6" mb="2">
          1. Basic Terms
        </Heading>
        <Text mb="4">
          You are responsible for your use of the Services and for any Content you provide,
          including compliance with applicable laws, rules, and regulations. You should only provide
          Content that you are comfortable sharing with others.
        </Text>

        {/* Age Restriction */}
        <Heading as="h2" size="lg" mt="6" mb="2">
          2. Age Restriction
        </Heading>
        <Text mb="4">
          The Services are intended for adults only. You must be at least 18 years old or the age of
          majority in your jurisdiction, whichever is greater, to use our Services. By using the
          Services, you represent and warrant that you meet this requirement. If you do not meet
          this requirement, you must not access or use the Services.
        </Text>

        {/* Privacy */}
        <Heading as="h2" size="lg" mt="6" mb="2">
          3. Privacy
        </Heading>
        <Text mb="4">
          Our Privacy Policy describes how we handle the information you provide to us when you use
          our Services. By using the Services, you consent to the collection and use of this
          information as described below:
        </Text>
        <Text mb="4">
          <strong>3.1 Information Collection:</strong> We collect various types of information, including:
        </Text>
        <List spacing={2} mb="4">
          <ListItem><strong>Personal Information:</strong> Email address</ListItem>
          <ListItem><strong>Usage Data:</strong> IP address, browser type, pages visited, and visit times.</ListItem>
          <ListItem><strong>Content Data:</strong> Text and other materials you upload or generate.</ListItem>
        </List>
        <Text mb="4">
          <strong>3.2 Use of Information:</strong> Collected information is used to provide, maintain, and improve
          our Services, personalize your experience, communicate with you, and enhance security.
        </Text>
        <Text mb="4">
          <strong>3.3 Data Storage and Security:</strong> We implement measures to protect your information.
          However, no security system is perfect. Importantly, we do not store any images on our
          servers. All images are processed and then permanently deleted.
        </Text>
        <Text mb="4">
          <strong>3.4 Data Sharing and Disclosure:</strong> We do not sell or rent your personal information. We may
          share your information with service providers, in response to legal requirements, or
          during business transfers.
        </Text>
        <Text mb="4">
          <strong>3.5 Account Deletion:</strong> You can request the deletion of your personal information by
          contacting us at info@deepmode.com. You can also delete your account at any time and all
          data associated with it by visiting your profile page and clicking on &quot;Delete Account&quot; at
          the bottom of the page: https://deepmode.com/profile
        </Text>
        <Text mb="4">
          <strong>3.6 Children&apos;s Privacy:</strong> The Services are not intended for those under 18. We do not
          knowingly collect information from children. If we discover that a child has provided us
          with personal information, we will delete it.
        </Text>
        <Text mb="4">
          <strong>3.7 International Data Transfers:</strong> Your information may be processed in other countries,
          and we ensure it receives adequate protection.
        </Text>
        <Text mb="4">
          By using the Services, you acknowledge that you have read and understood our Privacy Policy
          and agree to the collection, use, and sharing of your information as described. For any
          questions, contact us at info@deepmode.com.
        </Text>

        {/* Submitting Photos and Training AI Models */}
        <Heading as="h2" size="lg" mt="6" mb="2">
          4. Submitting Photos and Training AI Models
        </Heading>
        <Text mb="4">
          By using our Services, you agree that any images you submit for the purpose of training AI
          models belong to you and you have the right to use them. Uploading photos of minors is
          strictly forbidden on Deepmode.com. We take this matter very seriously and have implemented
          measures to monitor and prevent the upload of such content. Any attempt to upload photos
          of minors is a violation of these Terms and is subject to immediate action, including but
          not limited to the suspension or termination of your account and potential legal
          consequences. We are committed to ensuring a safe and legal environment on our platform
          and will cooperate with law enforcement if we discover any unlawful activity.
        </Text>

        {/* Use of the Services */}
        <Heading as="h2" size="lg" mt="6" mb="2">
          5. Use of the Services
        </Heading>
        <Text mb="4">
          You may use the Services only if you agree to form a binding contract with Deepmode.com, a
          service provided by DEEPMODE LTD, and are not a person barred from receiving services
          under the laws of the applicable jurisdiction. You may not use the Services if you do not
          accept the Terms.
        </Text>
        <Text mb="4">
          <strong>5.1 Limited License:</strong> DeepMode.com grants you a limited, non-exclusive, non-transferable,
          non-sublicensable, revocable license to use the platform to create content solely for your
          personal, non-commercial use.
        </Text>
        <Text mb="4">
          For inquiries regarding commercial use licenses, including the process and criteria for
          obtaining such permission, please contact us at info@deepmode.com.
        </Text>

        {/* Refund/Cancellation Policy */}
        <Heading as="h2" size="lg" mt="6" mb="2">
          6. Refund/Cancellation Policy
        </Heading>
        <Text mb="4">
          If you are dissatisfied with our Services or if you wish to cancel your subscription, you
          may be eligible for a refund within 7 days of purchase. Refund requests must be made by
          contacting our customer support team at info@deepmode.com. Please note that any virtual
          currency (&quot;Credits&quot;) purchased within the platform are non-refundable. We reserve the
          right to deny refund requests if we believe there is evidence of misuse or violation of
          these Terms.
        </Text>

        {/* Restrictions on Content and Use of the Services */}
        <Heading as="h2" size="lg" mt="6" mb="2">
          7. Restrictions on Content and Use of the Services
        </Heading>
        <Text mb="4">
          We manually review and moderate content. We reserve the right to remove or refuse to
          distribute any Content on the Services and to suspend or terminate users without liability
          to you, especially if they violate our policies.
        </Text>

        {/* Payment Terms */}
        <Heading as="h2" size="lg" mt="6" mb="2">
          8. Payment Terms
        </Heading>
        <Text mb="4">
          Certain aspects of the Service may be provided for a fee or other charge. If you elect to
          use paid aspects of the Service, you agree to the pricing and payment terms. We may add
          new services for additional fees and charges, or amend fees and charges for existing
          services, at any time in our sole discretion.
        </Text>

        {/* Disclaimers and Limitations of Liability */}
        <Heading as="h2" size="lg" mt="6" mb="2">
          9. Disclaimers and Limitations of Liability
        </Heading>
        <Text mb="4">
          The Services are available &quot;AS-IS&quot;. Your access to and use of the Services or any
          Content are at your own risk. You understand and agree that the Services are provided to
          you on an &quot;AS IS&quot; and &quot;AS AVAILABLE&quot; basis.
        </Text>

        {/* Termination */}
        <Heading as="h2" size="lg" mt="6" mb="2">
          10. Termination
        </Heading>
        <Text mb="4">
          We may suspend or terminate your account or cease providing you with all or part of the
          Services at any time for any reason, including, but not limited to, if we reasonably
          believe: (i) you have violated these Terms, (ii) you create risk or possible legal
          exposure for us; or (iii) our provision of the Services to you is no longer commercially
          viable.
        </Text>

        {/* General */}
        <Heading as="h2" size="lg" mt="6" mb="2">
          11. General
        </Heading>
        <Text mb="4">
          We may revise these Terms from time to time. Changes will not be retroactive, and the most
          current version of the Terms will govern our relationship with you. By continuing to
          access or use the Services after those revisions become effective, you agree to be bound
          by the revised Terms.
        </Text>

        {/* Updated Jurisdiction to Reflect New Document Content */}
        <Heading as="h2" size="lg" mt="6" mb="2">
          12. Jurisdiction
        </Heading>
        <Text mb="4">
          Terms are governed by the laws of Cyprus. Any disputes arising from or relating to the
          Terms or the Services will be subject to the exclusive jurisdiction of the courts of Cyprus.
        </Text>

        <Heading as="h2" size="lg" mt="6" mb="2">
          13. Data Handling Upon Account Termination
        </Heading>
        <Text mb="4">
          Upon termination of your account, either by you or by us, all your data and content will
          be permanently deleted from our systems.
        </Text>

        {/* Contact */}
        <Heading as="h2" size="lg" mt="6" mb="2">
          14. Contact
        </Heading>
        <Text>
          If you have any questions about these Terms, please contact us at:{" "}
          <Link color="blue.500" href="mailto:info@deepmode.com">
            info@deepmode.com
          </Link>
          .
        </Text>
        <Heading as="h2" size="lg" mt="6" mb="2">
          15. Company Information 
        </Heading>
        <VStack align="start" mb="4">
        <Text>Nefertiti Limited is registered in Hong Kong with the following details:</Text>

        <List spacing={2} mb="4">
          <ListItem>Registration number: 3092507          </ListItem>
        
          <ListItem> Registered business address: Room B, 7/F, One Capital Place
18 Luard Road
Wan Chai
Hong Kong
          </ListItem>
        </List>
        </VStack>

      </Box>
    </Flex>

  );
};

export default TermsAndConditions;
